import type { CSSProperties } from 'react'
import { ExpenseBar, ExpenseRing } from '../../components/ExpenseChart'
import { TulipPrinzip, TulipSketch } from '../../components/TulipSketch'
import {
  anteilBezug,
  anteilProzent,
  expenseSums,
  ringSegments,
  sumTotal,
} from '../../lib/expenseChart'
import { anzahl, money, num, zeile } from '../../lib/format'
import { TULIP_SHAPES } from '../../lib/tulip'
import { fahrzeug } from '../../lib/vehicle'
import { WEATHER_ATTRIBUTION } from '../../lib/weather'
import type { Checks, Expense, FuelKind, Trip } from '../../lib/types'
import { ATTRIBUTION } from '../map/basemap'
import { germanDate, type PrintMetrics } from './metrics'
import {
  CHECKS_PLAETZE_AUSRUESTUNG,
  CHECKS_PLAETZE_VIGNETTEN,
  checklistenSeite,
  legendeSeiten,
} from './sections'
import { NotizFeld, NotizLinien, PageFrame } from './PageFrame'

/**
 * Was hinter den Etappen kommt: Budget, Checklisten, Notizseiten, Rückseite.
 *
 * Alle Farbflächen dieser Seiten sind SVG und keine Hintergründe. Der
 * Unterschied entscheidet, ob sie überhaupt gedruckt werden:
 * Hintergrundfarben lässt der Browser nur aufs Papier, wenn im Druckdialog
 * "Hintergrundgrafiken" angehakt ist, Füllungen und Striche in einem
 * SVG-Element dagegen immer. Ein Ringdiagramm, das je nach Haken da ist oder
 * nicht, wäre im gebundenen Buch ein Ärgernis.
 */

interface BudgetProps {
  expenses: readonly Expense[]
  metrics: PrintMetrics
  currency: string
  /**
   * Die Kraftstoffart der Reise.
   *
   * Sie entscheidet, welche Kategorie im Ring die Kraftstofffarbe bekommt -
   * „Diesel“ soll dort nicht als namenlose Restkategorie stehen, nur weil die
   * Farbtabelle einmal fest „Benzin“ hiess.
   */
  fuel: FuelKind
  page: number
  fuss: string
}

export function BudgetPage({ expenses, metrics, currency, fuel, page, fuss }: BudgetProps) {
  // Zahlen und Ringgeometrie kommen aus lib/expenseChart.ts, gezeichnet wird
  // mit denselben Bauteilen wie am Bildschirm. Was hier steht, ist die Setzung
  // fuers Papier - Reihenfolge, Klassen, was daneben steht.
  const summen = expenseSums(expenses, fuel)
  // gesamt ist die Auskunft - "ausgegeben", mit Vorzeichen -, bezug der
  // Massstab der Zeichnung. Beides ist dasselbe, solange keine Kategorie unterm
  // Strich negativ ist; ist eine es (eine Erstattung), rettet der Unterschied
  // den Ring vor sich selbst. Siehe anteilBezug in lib/expenseChart.ts.
  const gesamt = sumTotal(summen)
  const bezug = anteilBezug(summen)
  const proTag = metrics.drivenCount > 0 ? gesamt / metrics.drivenCount : 0
  const segmente = ringSegments(summen, bezug)

  return (
    <PageFrame kind="budget" page={page} fuss={fuss}>
      <h2 className="dr-titelzeile">Was es gekostet hat</h2>

      <div className="dr-budgetkopf">
        <ExpenseRing
          segmente={segmente}
          praefix="dr-ring"
          label="Ausgaben je Kategorie"
          wert={num(gesamt)}
          text={currency}
        />

        <dl className="dr-budgetzahlen">
          <div>
            <dt>ausgegeben</dt>
            <dd>{money(gesamt, currency)}</dd>
          </div>
          <div>
            <dt>je gefahrenem Tag</dt>
            <dd>{money(proTag, currency)}</dd>
          </div>
          <div>
            <dt>Kraftstoff geschätzt</dt>
            <dd>{money(metrics.fuelCost, currency)}</dd>
          </div>
        </dl>
      </div>

      <ul className="dr-budgetliste">
        {summen.map((eintrag) => {
          const anteil = anteilProzent(eintrag.value, bezug)
          return (
            <li key={eintrag.key}>
              <span className="dr-name">{eintrag.key}</span>
              <ExpenseBar anteil={anteil} farbe={eintrag.color} className="dr-balken" />
              <span className="dr-anteil">{Math.round(anteil)} %</span>
              <span className="dr-betrag">{money(eintrag.value, currency)}</span>
            </li>
          )
        })}
        {summen.length === 0 && <li className="dr-leer">Nichts erfasst.</li>}
      </ul>

      <NotizFeld titel="Quittungen und offene Posten" />
    </PageFrame>
  )
}

interface ChecklistProps {
  checks: Checks
  kind: 'vignetten' | 'ausruestung'
  page: number
  fuss: string
}

/**
 * Eine Checkliste mit dem Stand ihrer Haken.
 *
 * Das Kästchen ist ein Rahmen mit einem Haken darin, keine Hintergrundfläche:
 * so ist auf dem Papier zu sehen, was schon erledigt war - und was nicht, lässt
 * sich unterwegs mit dem Kugelschreiber nachtragen.
 *
 * Wie viele Einträge auf die Seite passen, steht als CHECKS_PLAETZE_* in
 * sections.ts, samt der Messung dazu. Was darüber hinausgeht, verschwindet
 * nicht, sondern wird gezählt: die Listen sind in der App ergänzbar, und eine
 * Vignette für ein Land, das niemand eingeplant hatte, ist genau der Eintrag,
 * der hinten dransteht.
 */
export function ChecklistPage({ checks, kind, page, fuss }: ChecklistProps) {
  const vignetten = kind === 'vignetten'
  const alle = vignetten
    ? checks.vignettes.map((eintrag) => ({
        titel: eintrag.country,
        preis: eintrag.price,
        note: eintrag.note,
        done: eintrag.done,
      }))
    : checks.kit.map((eintrag) => ({
        titel: eintrag.name,
        preis: '',
        note: '',
        done: eintrag.done,
      }))

  const { gezeigt, rest } = checklistenSeite(
    alle,
    vignetten ? CHECKS_PLAETZE_VIGNETTEN : CHECKS_PLAETZE_AUSRUESTUNG,
  )
  // Der Zähler zählt über die ganze Liste und nicht über die gedruckte Seite:
  // er sagt, wie viel vor der Abfahrt erledigt ist, und das ist eine Aussage
  // über die Reise. Dass die Seite nicht alles zeigt, sagt die Restzeile.
  const erledigt = alle.filter((eintrag) => eintrag.done).length

  return (
    <PageFrame kind={kind} page={page} fuss={fuss}>
      <h2 className="dr-titelzeile">
        {vignetten ? 'Vignetten und Maut' : 'Ersatzteile und Papiere'}
        <span className="dr-zaehler">
          {erledigt} / {alle.length}
        </span>
      </h2>

      <ul className="dr-checkliste">
        {gezeigt.map((eintrag, i) => (
          <li key={i}>
            <span className="dr-haken" aria-hidden="true">
              {eintrag.done ? '✓' : ''}
            </span>
            <span className="dr-check-text">
              <span className="dr-check-kopf">
                <b>{eintrag.titel || '—'}</b>
                {eintrag.preis && <span className="dr-preis"> {eintrag.preis}</span>}
              </span>
              {eintrag.note && <small>{eintrag.note}</small>}
            </span>
          </li>
        ))}
        {rest > 0 && <li className="dr-mehr">und {rest} weitere</li>}
        {alle.length === 0 && <li className="dr-leer">Die Liste ist leer.</li>}
      </ul>

      {/*
        Was die Liste nicht füllt, wird zum Platz für das, was unterwegs
        dazukommt - genau der Fall, um den es bei den ergänzbaren Listen geht:
        eine Vignette für ein Land, das niemand eingeplant hatte.
      */}
      <NotizFeld titel="Nachgetragen" />
    </PageFrame>
  )
}

/**
 * Die Legende: was die Zeichen im Roadbook bedeuten.
 *
 * Die Notation stammt aus dem Rallyesport und ist ausserhalb davon niemandem
 * geläufig. Wer im Auto sitzt und zum ersten Mal eine Tulpe sieht, sieht einen
 * Kreis, ein paar Striche und einen Pfeil - und hat keine Zeit, sich das
 * herzuleiten. Deshalb steht die Erklärung im Buch und nicht in der App: das
 * Buch ist es, das im Handschuhfach liegt.
 *
 * Zwei Seiten, und die erste trägt oben das Bauprinzip an einem Zeichen erklärt
 * - Punkt, dicker Weg, Pfeil, dünne Striche daneben. Damit lässt sich jedes der
 * fünfzehn lesen, auch eines, das man noch nie gesehen hat. Darunter stehen sie
 * einzeln, mit Namen und einem Satz; die sind zum Nachschlagen und nicht zum
 * Lernen.
 *
 * Welches Zeichen auf welche Seite kommt, entscheidet legendeSeiten in
 * sections.ts und nicht diese Datei: es ist eine Aussage über den Katalog, und
 * sie wird dort geprüft, wo auch die Plätze je Seite stehen.
 *
 * Abgedruckt werden alle Formen des Katalogs und nicht nur die, die in dieser
 * Reise vorkommen. Das Buch fährt mit, die Reise weicht von der Planung ab, und
 * die Form, die unterwegs mit dem Kugelschreiber in eine leere Zeile kommt, ist
 * immer die, die vorher niemand gebraucht hat.
 *
 * @param part Die wievielte Legendenseite, von 1 an.
 */
export function LegendPage({ part, page, fuss }: { part: number; page: number; fuss: string }) {
  const seiten = legendeSeiten(TULIP_SHAPES)
  const seite = seiten[part - 1]
  if (!seite) return null

  return (
    <PageFrame kind="legende" page={page} fuss={fuss}>
      <h2 className="dr-titelzeile">
        {seite.mitTafel ? 'Die Zeichen im Roadbook' : 'Zeichen für eine Stelle der Strecke'}
      </h2>

      {seite.mitTafel && (
        <section className="dr-lehre">
          <TulipPrinzip />
          <div className="dr-lehre-text">
            <p>
              Jedes Zeichen wird von unten nach oben gelesen: unten kommt man an, oben geht es
              weiter.
            </p>
            {/*
              Der Kreis und die Seitenteilung sind zweierlei, und der Satz hat
              sie eine Zeit lang in einen geworfen: er sagte, Zeichen ohne
              Kreis stünden auf der nächsten Seite. Auf- und Abfahrt haben
              keinen Kreis - an einer Rampe gibt es keinen Knoten, an dem man
              stünde - und stehen trotzdem hier, denn sie nennen eine
              Richtung. Genau danach teilt legendeSeiten.
            */}
            <p>
              Ein Kreis ist eine Kreuzung, eine Insel darin ein Kreisverkehr. Auf dieser Seite
              stehen die Zeichen, die sagen, wohin es geht - auch die beiden Autobahnrampen, die
              ohne Kreis auskommen; auf der nächsten die, die eine Stelle der Strecke ankündigen.
            </p>
          </div>
        </section>
      )}

      {/*
        Die Reihenzahl steht als Eigenschaft am Element und nicht in print.css.
        Der Grund ist das spaltenweise Füllen: grid-auto-flow:column braucht die
        Höhe der Spalte als Zahl, und die hängt daran, wie viele Zeichen auf
        dieser Seite stehen. Eine feste Zahl im Stylesheet wäre genau die Art
        von stiller Kopplung, an der auf dieser Seite schon einmal ein Eintrag
        unter die Kante gerutscht ist. Wie viele Reihen eine Seite höchstens
        trägt, steht dagegen weiterhin dort, wo es hingehört: als
        LEGENDE_PLAETZE_MIT_TAFEL und LEGENDE_PLAETZE_OHNE_TAFEL in sections.ts,
        gehalten von einem Test.
      */}
      <ul
        className="dr-legende"
        style={{ '--dr-legende-reihen': Math.ceil(seite.zeichen.length / 2) } as CSSProperties}
      >
        {seite.zeichen.map((info) => (
          <li key={info.key}>
            {/*
              Gezeichnet wird die Form mit einer Richtung, die sie kennt: eine
              Gabelung mit einem Pfeil geradeaus wäre ein Zeichen, das es so im
              Buch nie gibt. exitDirection rastet ein, hier reicht deshalb ein
              beliebiger Wert - 90 Grad, also nach rechts, weil das die Seite
              ist, auf die man in Europa ohne Gegenverkehr abbiegt.
            */}
            <TulipSketch tulip={{ km: Number.NaN, shape: info.key, turn: 90, note: '' }} />
            <span className="dr-legende-text">
              <b>{info.label}</b>
              {info.legende}
            </span>
          </li>
        ))}
      </ul>
    </PageFrame>
  )
}

/** Eine linierte Seite zum Vollschreiben. */
export function NotesPage({ page, fuss }: { page: number; fuss: string }) {
  return (
    <PageFrame kind="notizen" page={page} fuss={fuss}>
      <h2 className="dr-titelzeile">Notizen</h2>
      <NotizLinien anzahl={34} />
    </PageFrame>
  )
}

/**
 * Die Rückseite: woher das Buch kommt und woher die Karten.
 *
 * Der Nachweis für die Kartendaten gehört hierher und nicht nur unter jede
 * Karte: die ODbL verlangt die Nennung der Quelle, und auf Papier gibt es
 * keinen Knopf, der sie nachträglich aufklappt.
 *
 * Dasselbe gilt für das Wetter, sobald welches geholt wurde: die Daten stehen
 * unter CC BY 4.0, die ebenfalls eine Nennung verlangt. Sie steht nur dann da,
 * wenn auch Werte im Buch sein können - eine Quellenangabe für etwas, das
 * nirgends vorkommt, ist keine Sorgfalt, sondern Beiwerk.
 */
export function BackPage({
  trip,
  gedrucktAm,
  mitWetter = false,
}: {
  trip: Trip
  gedrucktAm: string
  /** Hat irgendeine Etappe Wetterwerte aus der Aufzeichnung? */
  mitWetter?: boolean
}) {
  const auto = fahrzeug(trip)
  // Wie im Kopf der App, und aus demselben Grund: das Nationalitätszeichen
  // kommt vom Fahrzeug und stand hier fest als CH - auf dem gedruckten
  // Buchrücken, der mit den Mitreisenden geteilt wird. Ohne Zeichen und ohne
  // Namen bleibt das Oval weg.
  const schild = zeile([auto.sign, auto.name.split(' ')[0]])

  return (
    <PageFrame kind="rueckseite">
      <div className="dr-rueck">
        {/* Wie im Kopf der App: kein Trennpunkt vor dem Nichts, und die
            Zahlform richtet sich nach der Zahl. */}
        {schild && <span className="dr-schild">{schild}</span>}
        <p className="dr-rueck-titel">{trip.meta.title || 'Roadbook'}</p>
        <p className="dr-rueck-zeile">
          {zeile([auto.name, anzahl(trip.meta.crew, 'Person', 'Personen')])}
        </p>
        <p className="dr-rueck-klein">
          Gedruckt aus dem Roadbook am {germanDate(gedrucktAm)}. Kartendaten {ATTRIBUTION}, unter
          der Open Database License. {mitWetter && `${WEATHER_ATTRIBUTION}. `}
          Die Zahlen stammen aus der Planung und dem Tagebuch dieser Reise; wo beides
          auseinandergeht, gilt der Tachostand.
        </p>
      </div>
    </PageFrame>
  )
}
